import { useCallback, useEffect, useMemo } from 'react';
import { useCadCanvasImageDrag } from './useCadCanvasImageDrag';
import { setCadCanvasImageCoordinates, useCadCanvasImageLayer } from './useCadCanvasImageLayer';
import { useCadImageNaturalSize } from './useCadImageNaturalSize';
import {
  cadCanvasImageMeterCorners,
  clampCadCanvasImageTransform,
  isCadCanvasImageTransformDirty,
  readCadCanvasImage,
  readCadCanvasImageMapCoordinates,
  readCadCanvasImageSizeHint,
  readCadCanvasImageTransform,
  resolveCadMapCanvasImageCoordinates,
  readCadShowPlanImage,
  shouldPaintCadMapImage,
  writeCadCanvasImageSizeHint,
} from '../utils/cadCanvasImage';
import {
  projectCadSrcWithSticker,
  readCadGeorefOverlayOrigin,
  readCadStickerSrcBounds,
  unprojectCadLngLatWithSticker,
} from '../utils/cadGeorefSticker';

/**
 * PNG debajo del CAD en el mapa país.
 *
 * **No romper:** dueño, visitante e incógnito usan la misma proyección que
 * Referenciar (`cadCanvasImageMeterCorners` + sticker). Si al Guardar se
 * pinta `canvas_image.map_coordinates`, el PNG salta: el GET hornea otro
 * contain. `resolveCadMapCanvasImageCoordinates` elige live primero.
 * `docs/maps/cad-showroom.md` (imagen base).
 *
 * Con `sticker` explícito (preview de Referenciar) se pinta aunque la lista
 * todavía no esté en `map`, si `show_plan_image` lo permite. Ajustar imagen
 * fuerza el PNG para encajarlo aunque el switch esté apagado.
 *
 * @param {{
 *   mapInstanceRef: { current: import('maplibre-gl').Map|null },
 *   enabled?: boolean,
 *   project?: object|null,
 *   sticker?: object|null,
 *   transform?: object|null,
 *   paintUrl?: string|null,
 *   onTransformChange?: (next: object) => void,
 *   adjustEnabled?: boolean,
 * }} options
 */
export function useCadMapCanvasImage({
  mapInstanceRef,
  enabled = false,
  project = null,
  sticker = null,
  transform = null,
  paintUrl = null,
  onTransformChange,
  adjustEnabled = false,
}) {
  const overlaySticker = sticker || readCadGeorefOverlayOrigin(project);
  const srcBounds = overlaySticker?.srcBounds || readCadStickerSrcBounds(project);
  const mapCoordinates = useMemo(
    () => readCadCanvasImageMapCoordinates(project),
    [project],
  );
  const storedPose = useMemo(
    () => readCadCanvasImageTransform(project),
    [project],
  );
  const wantPaint = adjustEnabled
    || shouldPaintCadMapImage(project)
    || (Boolean(sticker) && readCadShowPlanImage(project));
  const remote = wantPaint ? readCadCanvasImage(project) : null;
  const url = wantPaint ? (paintUrl || remote?.url || null) : null;
  const sizeHint = useMemo(() => {
    const src = typeof paintUrl === 'string' ? paintUrl.trim() : '';
    if (src.startsWith('blob:') || src.startsWith('data:')) return null;
    return readCadCanvasImageSizeHint(project);
  }, [paintUrl, project]);
  const imageSize = useCadImageNaturalSize(url, sizeHint);
  const pose = useMemo(
    () => {
      const raw = adjustEnabled || isCadCanvasImageTransformDirty(transform)
        ? transform
        : storedPose;
      return clampCadCanvasImageTransform(raw, srcBounds, imageSize);
    },
    [adjustEnabled, imageSize, srcBounds, storedPose, transform],
  );

  const projectPose = useCallback((nextPose) => {
    if (!url || !overlaySticker || !srcBounds) return null;
    const meters = cadCanvasImageMeterCorners(srcBounds, imageSize, nextPose);
    if (!meters) return null;
    const projected = meters.map(([x, y]) => projectCadSrcWithSticker(x, y, overlaySticker));
    return projected.every(Boolean) ? projected : null;
  }, [imageSize, overlaySticker, srcBounds, url]);

  const liveCoordinates = useMemo(
    () => projectPose(pose),
    [pose, projectPose],
  );
  // Live primero. Preferir map_coordinates acá es la regresión de Guardar.
  const coordinates = useMemo(
    () => resolveCadMapCanvasImageCoordinates({
      mapCoordinates,
      liveCoordinates,
    }),
    [liveCoordinates, mapCoordinates],
  );

  useEffect(() => {
    if (imageSize) writeCadCanvasImageSizeHint(project, imageSize);
  }, [imageSize, project]);

  useCadCanvasImageLayer({
    mapInstanceRef,
    enabled: enabled && Boolean(url),
    url,
    coordinates,
    guideMode: adjustEnabled,
  });

  const unproject = useCallback(
    (lng, lat) => unprojectCadLngLatWithSticker(lng, lat, overlaySticker),
    [overlaySticker],
  );

  const applyLivePose = useCallback((nextPose) => {
    const next = projectPose(clampCadCanvasImageTransform(nextPose, srcBounds, imageSize));
    if (next) setCadCanvasImageCoordinates(mapInstanceRef.current, next);
  }, [imageSize, mapInstanceRef, projectPose, srcBounds]);

  useEffect(() => {
    if (!adjustEnabled) return;
    applyLivePose(pose);
  }, [adjustEnabled, applyLivePose, pose]);

  const handleTransformChange = useCallback((nextPose, options) => {
    onTransformChange?.(
      clampCadCanvasImageTransform(nextPose, srcBounds, imageSize),
      options,
    );
  }, [imageSize, onTransformChange, srcBounds]);

  useCadCanvasImageDrag({
    mapInstanceRef,
    enabled: enabled && adjustEnabled && Boolean(url),
    pose,
    unproject,
    onLiveTransform: applyLivePose,
    onTransformChange: handleTransformChange,
  });
}
